import { Scale } from '@/services/canvas/Scale';
import { isValidContext } from '@/modules/map-editor';
import type { MapAreaState, ImageDimensions, MapImageConfig, DrawAreaOverride } from '@/modules/map-editor';
import { BACKGROUND } from '@/models/BACKGROUND';

/**
 * Service for rendering map area and background image
 */
export class MapAreaRenderer {
  drawMapArea(
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapArea: MapAreaState,
    ignoreMapCoords = false,
    area: DrawAreaOverride = { width: null, height: null, shiftX: 0, shiftY: 0 }
  ): void {
    if (!isValidContext(ctx)) {
      console.warn('Invalid canvas context for drawing map area');
      return;
    }

    const { width, height, shiftX, shiftY } = area;
    const mapShiftX = ignoreMapCoords ? (width ?? 0) / 2 + Math.abs(shiftX) : mapArea.x;
    const mapShiftY = ignoreMapCoords ? (height ?? 0) / 2 + Math.abs(shiftY) : mapArea.y;

    const halfW = ctx.canvas.width / 2;
    const halfH = ctx.canvas.height / 2;
    const scaledX = Scale.x(mapShiftX, halfW, zoomLevel);
    const scaledY = Scale.y(mapShiftY, halfH, zoomLevel);
    const scaledW = mapArea.width * zoomLevel;
    const scaledH = mapArea.height * zoomLevel;

    ctx.strokeStyle = mapArea.borderColor;
    ctx.setLineDash([2, 4]);
    ctx.lineDashOffset = 25;
    ctx.beginPath();
    ctx.strokeRect(scaledX, scaledY, scaledW, scaledH);
    ctx.closePath();
  }

  drawMapImage(
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapArea: MapAreaState,
    mapData: MapImageConfig
  ): void {
    if (!isValidContext(ctx)) {
      console.warn('Invalid canvas context for drawing map image');
      return;
    }

    if (!mapData.image || !mapData.image.width) {
      return;
    }

    const halfW = ctx.canvas.width / 2;
    const halfH = ctx.canvas.height / 2;
    const clipX = Scale.x(mapArea.x, halfW, zoomLevel);
    const clipY = Scale.y(mapArea.y, halfH, zoomLevel);
    const clipW = mapArea.width * zoomLevel;
    const clipH = mapArea.height * zoomLevel;

    ctx.save();
    ctx.beginPath();

    // Create a non-primitive path to be able to clip image
    ctx.rect(clipX, clipY, clipW, clipH);
    ctx.clip();

    const imageDimensions = this.getImageDimensions(mapData.image, mapData);
    this.setImagePosition(imageDimensions, mapData, mapArea);

    ctx.globalAlpha = mapData.mediaOpacity / 100;

    ctx.drawImage(
      mapData.image,
      Scale.x(mapArea.x + imageDimensions.x, halfW, zoomLevel),
      Scale.y(mapArea.y + imageDimensions.y, halfH, zoomLevel),
      imageDimensions.width * zoomLevel,
      imageDimensions.height * zoomLevel
    );

    ctx.restore();
  }

  getImageDimensions(image: HTMLImageElement, mapData: MapImageConfig): ImageDimensions {
    const imageWidth = image.width;
    const imageHeight = image.height;
    const imageRatio = imageWidth / imageHeight;
    const mapAreaWidth = mapData.width;
    const mapAreaHeight = mapData.height;

    const mapAreaWidthProportionalToImage = mapAreaWidth / imageRatio;

    const imageDimensions: ImageDimensions = {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };

    // Calculate dimensions based on background size
    switch (mapData.mediaSize) {
      case BACKGROUND.SIZE.FILL.VALUE:
        // Stretch image to whole available mapArea ignoring an image ratio
        imageDimensions.x = 0;
        imageDimensions.y = 0;
        imageDimensions.width = mapAreaWidth;
        imageDimensions.height = mapAreaHeight;
        break;

      case BACKGROUND.SIZE.CONTAIN.VALUE:
        if (mapAreaWidthProportionalToImage < mapAreaHeight) {
          imageDimensions.width = mapAreaWidth;
          imageDimensions.height = mapAreaWidth / imageRatio;
        } else {
          imageDimensions.width = mapAreaHeight * imageRatio;
          imageDimensions.height = mapAreaHeight;
        }
        imageDimensions.y = (mapAreaHeight - imageDimensions.height) / 2;
        break;

      case BACKGROUND.SIZE.COVER.VALUE:
        if (mapAreaWidthProportionalToImage > mapAreaHeight) {
          imageDimensions.width = mapAreaWidth;
          imageDimensions.height = mapAreaWidth / imageRatio;
        } else {
          imageDimensions.width = mapAreaHeight * imageRatio;
          imageDimensions.height = mapAreaHeight;
        }
        imageDimensions.y = (mapAreaHeight - imageDimensions.height) / 2;
        break;

      case BACKGROUND.SIZE.NONE.VALUE:
        imageDimensions.x = (mapAreaWidth - imageWidth) / 2;
        imageDimensions.y = (mapAreaHeight - imageHeight) / 2;
        imageDimensions.width = imageWidth;
        imageDimensions.height = imageHeight;
        break;
    }

    return imageDimensions;
  }

  setImagePosition(
    imageDimensions: ImageDimensions,
    mapData: MapImageConfig,
    mapArea: MapAreaState
  ): void {
    switch (mapData.mediaPosition) {
      case BACKGROUND.POSITION.CENTER.VALUE:
        imageDimensions.x = (mapArea.width - imageDimensions.width) / 2;
        break;

      case BACKGROUND.POSITION.LEFT.VALUE:
        imageDimensions.x = 0;
        break;

      case BACKGROUND.POSITION.RIGHT.VALUE:
        imageDimensions.x = (mapArea.width - imageDimensions.width);
        break;

      // Default is center
      default:
        imageDimensions.x = (mapArea.width - imageDimensions.width) / 2;
        break;
    }
  }
}